// Thin WebSocket wrapper for the world protocol. Auto-reconnects, queues
// messages while disconnected, and exposes a typed `on(type, handler)` API.
//
// The client only ever has one socket, there's no parallel duel socket; the
// duel handshake will be muxed through this same connection.

import { type ClientMessage, type ServerMessage } from "@ygoevo/world-protocol";

type Handler<K extends ServerMessage["t"]> = (msg: Extract<ServerMessage, { t: K }>) => void;

export class WorldSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private queue: string[] = [];
  private handlers = new Map<ServerMessage["t"], Set<(m: ServerMessage) => void>>();
  private openHandlers = new Set<() => void>();
  private closeHandlers = new Set<() => void>();
  private reconnectMs = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(url: string) {
    this.url = url;
  }

  /** Drop the current connection and open a fresh one, used after sign-in /
   *  sign-out so the new WS upgrade carries the updated better-auth cookie (the
   *  server authenticates from it at connect time). */
  reconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const old = this.ws;
    this.ws = null;
    if (old) {
      const wasOpen = old.readyState === WebSocket.OPEN;
      try {
        old.close();
      } catch {
        /* ignore */
      }
      if (wasOpen) for (const h of this.closeHandlers) h();
    }
    // The old socket's close handler is ignored because it is no longer current.
    this.connect();
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.addEventListener("open", () => {
      if (this.ws !== ws) return;
      this.reconnectMs = 1000;
      // Authentication is installed as an open handler. Run it before replaying
      // actions queued during the outage, otherwise the server quite correctly
      // rejects those actions as anonymous "invalid input".
      for (const h of this.openHandlers) h();
      for (const q of this.queue.splice(0)) ws.send(q);
    });
    ws.addEventListener("message", (e) => {
      if (this.ws !== ws) return;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(e.data as string) as ServerMessage;
      } catch {
        return;
      }
      const set = this.handlers.get(msg.t);
      if (!set) return;
      for (const h of set) h(msg);
    });
    ws.addEventListener("close", () => {
      if (this.ws !== ws) return;
      this.ws = null;
      for (const h of this.closeHandlers) h();
      const delay = this.reconnectMs;
      this.reconnectMs = Math.min(this.reconnectMs * 2, 10_000);
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, delay);
    });
    ws.addEventListener("error", () => {
      // Let `close` handle reconnect, error always fires close right after.
    });
  }

  send(m: ClientMessage): void {
    const wire = JSON.stringify(m);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(wire);
    else this.queue.push(wire);
  }

  on<K extends ServerMessage["t"]>(t: K, h: Handler<K>): () => void {
    let set = this.handlers.get(t);
    if (!set) {
      set = new Set();
      this.handlers.set(t, set);
    }
    set.add(h as (m: ServerMessage) => void);
    return () => set!.delete(h as (m: ServerMessage) => void);
  }

  onOpen(h: () => void): () => void {
    this.openHandlers.add(h);
    return () => this.openHandlers.delete(h);
  }

  onClose(h: () => void): () => void {
    this.closeHandlers.add(h);
    return () => this.closeHandlers.delete(h);
  }

  /** Dev/test seam: deliver a server message to local handlers as if it had arrived
   *  over the wire. Used by the offline `?…mock=1` design hooks; never called in prod. */
  deliverLocal(m: ServerMessage): void {
    const set = this.handlers.get(m.t);
    if (!set) return;
    for (const h of set) h(m);
  }
}
